import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { usePageMeta } from "../lib/meta";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, formatError, setToken } from "../lib/api";

const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setMsg((await api.post("/auth/forgot-password", { email })).data.message); } catch (err) { setMsg(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="forgot-password-form">
      <p>Enter your account email and we'll send you a link to reset your password.</p>
      <input data-testid="forgot-email-input" type="email" placeholder="Account email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <button className="button primary full" data-testid="forgot-submit-button" disabled={busy}>Send reset link <ArrowRight size={17} /></button>
      {msg && <div className="info-note" data-testid="forgot-message">{msg}</div>}
      <button type="button" className="text-link-button" data-testid="forgot-back-button" onClick={onBack}>Back to sign in</button>
    </form>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [state, setState] = useState({ error: "", done: false });
  usePageMeta("Reset password");
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setState({ error: "Passwords do not match", done: false });
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", new_password: form.password }); setState({ error: "", done: true }); }
    catch (err) { setState({ error: formatError(err), done: false }); }
  };
  return (
    <main className="auth-page"><div className="auth-panel"><div className="section-kicker">ACCOUNT</div><h1>Choose a new<br /><span>password.</span></h1>
      {state.done ? <><div className="info-note" data-testid="reset-success">Your password has been updated.</div><Link className="button primary" data-testid="reset-signin-link" to="/login">Sign in <ArrowRight size={17} /></Link></> :
        <form onSubmit={submit}>
          <input data-testid="reset-password-input" type="password" minLength={10} placeholder="New password (10+ characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <input data-testid="reset-confirm-input" type="password" minLength={10} placeholder="Confirm new password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
          <button className="button primary full" data-testid="reset-submit-button">Update password <ArrowRight size={17} /></button>
          {state.error && <div className="error" data-testid="reset-error">{state.error}</div>}
        </form>}
    </div><div className="auth-side"><div className="auth-quote">“The strongest professional signal is the one that can be checked.”</div><span>AVAILITY / 2026</span></div></main>
  );
};

export const Auth = ({ setUser }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpInfo, setOtpInfo] = useState("");
  const [otp, setOtp] = useState("");
  const [signupToken, setSignupToken] = useState("");
  const navigate = useNavigate();
  usePageMeta(mode === "register" ? "Create account" : "Sign in", "Sign in to AVAILITY to take assessments and manage your certificates.");
  const signedIn = (data) => { setToken(data.token); setUser(data); navigate(data.role === "admin" ? "/admin" : "/dashboard"); };
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const r = await api.post(`/auth/${mode}`, form);
      if (r.data.otp_required) { setOtpInfo(r.data.message); setSignupToken(r.data.signup_token); setMode("otp"); } else signedIn(r.data);
    } catch (err) { setError(formatError(err)); } finally { setBusy(false); }
  };
  const verifyOtp = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { signedIn((await api.post("/auth/register/verify", { email: form.email, otp, signup_token: signupToken })).data); }
    catch (err) { setError(formatError(err)); } finally { setBusy(false); }
  };
  const resend = async () => {
    setError("");
    try { const r = await api.post("/auth/register", form); setOtpInfo(r.data.message); setSignupToken(r.data.signup_token); setOtp(""); toast.success("A new code was sent"); } catch (err) { setError(formatError(err)); }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const field = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <main className="auth-page"><div className="auth-panel"><div className="section-kicker">WELCOME TO AVAILITY</div>
      <h1>{mode === "login" ? "Continue your" : "Start your"}<br /><span>professional path.</span></h1>
      {mode === "otp" ? (
        <form onSubmit={verifyOtp} data-testid="otp-form">
          <div className="info-note" data-testid="otp-info">{otpInfo}</div>
          <input data-testid="otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="\d{6}" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} required />
          <button className="button primary full" data-testid="otp-submit-button" disabled={busy || otp.length !== 6}>Verify & create account <ArrowRight size={17} /></button>
          {error && <div className="error" data-testid="otp-error">{error}</div>}
          <div className="otp-links"><button type="button" className="text-link-button" data-testid="otp-resend-button" onClick={resend}>Resend code</button>
            <button type="button" className="text-link-button" data-testid="otp-back-button" onClick={() => { setMode("register"); setOtp(""); setError(""); }}>Change email</button></div>
        </form>
      ) : mode === "forgot" ? <ForgotPassword onBack={() => setMode("login")} /> : <>
      <p>{mode === "login" ? "Sign in to pick up where you left off." : "Create your account and make your expertise verifiable."}</p>
      <button className="button google" data-testid="google-login-button" onClick={google}><span className="g-mark">G</span> Continue with Google</button>
      <div className="divider"><span>or use email</span></div>
      <form onSubmit={submit}>
        {mode === "register" && <input data-testid="register-name-input" placeholder="Full name (as it should appear on your certificate)" value={form.name} onChange={field("name")} required />}
        <input data-testid={`${mode}-email-input`} type="email" placeholder="Enter your work or personal email ID" value={form.email} onChange={field("email")} required />
        <input data-testid={`${mode}-password-input`} type="password" minLength={mode === "register" ? 8 : undefined} placeholder="Password (8+ characters)" value={form.password} onChange={field("password")} required />
        <button className="button primary full" data-testid={`${mode}-submit-button`} disabled={busy}>{mode === "login" ? "Sign in" : "Send verification code"} <ArrowRight size={17} /></button>
        {error && <div className="error" data-testid="auth-error">{error}</div>}
        {mode === "login" && <button type="button" className="text-link-button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}>Forgot password?</button>}
      </form>
      <p className="switch">{mode === "login" ? "New to AVAILITY?" : "Already have an account?"} <button data-testid="auth-mode-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}>{mode === "login" ? "Create an account" : "Sign in"}</button></p></>}
    </div><div className="auth-side"><div className="auth-quote">“The strongest professional signal is the one that can be checked.”</div><span>AVAILITY / 2026</span></div></main>
  );
};

export const AuthCallback = ({ setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sessionId })
      .then((r) => { setToken(r.data.token); setUser(r.data); window.history.replaceState(null, "", location.pathname); navigate(r.data.role === "admin" ? "/admin" : "/dashboard", { replace: true }); })
      .catch((e) => setError(formatError(e)));
  }, [location, navigate, setUser]);
  if (!error) return null;
  return <main className="static-page"><div className="error" data-testid="google-callback-error">{error}</div><button className="text-link" data-testid="google-callback-retry" onClick={() => navigate("/login", { replace: true })}>Back to sign in <ArrowRight size={16} /></button></main>;
};
